import { OrganiserStructureType, Visitor } from "@ac-expo/api-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import {
  organiserExhibitorsQueryKeys,
  organiserVisitorsQueryKeys,
} from "../query-keys/organiser.query-keys";
import {
  AddOrganiserExhibitorForm,
  NewExhibitor,
  PartialAddVisitorFromDataForm,
} from "../schemas";

type ImportVisitorBody = {
  import: PartialAddVisitorFromDataForm[];
  deletedIds: string[];
};

const addOrganiserExhibitor = async (body: AddOrganiserExhibitorForm) => {
  const { data } = await privateAxios.post("/organiser/exhibitor", body);
  return data;
};

export const useAddOrganiserExhibitorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addOrganiserExhibitor,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organiserExhibitorsQueryKeys.all,
      });
    },
  });
};

type ImportOrganiserExhibitorsBody = {
  exhibitors: NewExhibitor[];
  exhibitorsToRemove: NewExhibitor[];
};

const importOrganiserExhibitors = async (
  rawData: ImportOrganiserExhibitorsBody,
) => {
  const data = {
    exhibitors: rawData.exhibitors,
    exhibitorsToRemove: rawData.exhibitorsToRemove.map((exhib) => ({
      id: exhib.saveId,
    })),
  };
  const { data: res } = await privateAxios.post(
    "/organiser/exhibitors/import",
    data,
  );
  return res;
};

export const useImportOrganiserExhibitorsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: importOrganiserExhibitors,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organiserExhibitorsQueryKeys.me(),
      });
    },
  });
};

export const addOrganiserVisitor = async (
  body: { connectId: string } | { data: PartialAddVisitorFromDataForm },
) => {
  const { data } = await privateAxios.post<Visitor>("/organiser/visitor", body);
  return data;
};

export const useAddOrganiserVisitorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addOrganiserVisitor,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organiserVisitorsQueryKeys.me(),
      });
    },
  });
};

export const importOrganiserVisitors = async (body: ImportVisitorBody) => {
  const { data } = await privateAxios.post("/organiser/visitors/import", body);
  return data;
};

export const useImportOrganiserVisitorsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: importOrganiserVisitors,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organiserVisitorsQueryKeys.me(),
      });
    },
  });
};

type UpdateExhibitorLocationBody = {
  stand?: string;
  hall?: string;
};

const updateExhibitorLocation = async (
  eventId: string,
  structureId: string,
  body: UpdateExhibitorLocationBody,
) => {
  const { data } = await privateAxios.patch<OrganiserStructureType>(
    `/organiser/events/${eventId}/exhibitors/${structureId}/location`,
    body,
  );
  return data;
};

export const useUpdateExhibitorLocationMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      structureId,
      ...body
    }: { structureId: string } & UpdateExhibitorLocationBody) =>
      updateExhibitorLocation(eventId, structureId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organiserExhibitorsQueryKeys.event(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: organiserExhibitorsQueryKeys.eventStructures(eventId),
      });
    },
  });
};
